import {
  useEffect,
  useCallback,
  Dispatch,
  SetStateAction,
  useRef,
} from "react";

interface User {
  id: string;
  name: string;
}

const LOGOUT_TIMER_DURATION = parseInt(
  process.env.LOGOUT_TIMER_DURATION || "1800000",
  10
); // 30 minutes in milliseconds

export const noop = () => {}; // ts-ignore

const useLogoutTimer = (
  user: User | null,
  setLogoutTimer: Dispatch<SetStateAction<NodeJS.Timeout | null>>,
  handleSignOut: () => void
): (() => void) => {
  const startLogoutTimerRef = useRef<() => void>(noop);

  const startLogoutTimer = useCallback(() => {
    const timer = setTimeout(() => {
      handleSignOut();
    }, LOGOUT_TIMER_DURATION);
    setLogoutTimer(timer);
  }, [setLogoutTimer, handleSignOut]);

  startLogoutTimerRef.current = startLogoutTimer;

  const resetLogoutTimer = useCallback(() => {
    startLogoutTimerRef.current();
  }, []);

  const clearLogoutTimer = useCallback(() => {
    setLogoutTimer(null);
  }, [setLogoutTimer]);

  useEffect(() => {
    const handleEvent = () => {
      resetLogoutTimer();
    };

    if (user) {
      startLogoutTimerRef.current();
      const events = ["keydown", "click"];
      events.forEach((event) => window.addEventListener(event, handleEvent));

      return () => {
        events.forEach((event) =>
          window.removeEventListener(event, handleEvent)
        );
        clearLogoutTimer();
      };
    }
  }, [user, resetLogoutTimer, clearLogoutTimer]);

  return resetLogoutTimer;
};

export default useLogoutTimer;
